// ─── Card-payment status classification ──────────────────────────────────────
// `POST /api/user/process-card-payment` returns HTTP 200 for outcomes that are
// NOT settled charges. The status field carries the real result:
//
//   "successful"     → the charge settled.
//   "failed"         → Chargebee/Stripe decline. Surface the message; retry is
//                      meaningful.
//   "needs_3ds"      → Stripe wants a bank challenge mid-flight.
//   "needs_new_card" → the saved card belongs to the OTHER Stripe account
//                      (Inhouse Inc vs. Inhouse Counsel PC). Nothing was
//                      charged and retrying the same card can never succeed;
//                      collect card details instead. This is NOT a decline.
//
// ─── Why this is an allowlist, and why it lives here ─────────────────────────
// The rule used to be a denylist (`status !== "failed" && status !== "needs_3ds"`)
// spread across four call sites: the MIT saved-card submit, the post-3DS retry,
// the new-card submit, and chatSlice's metadata-refresh gate. That shape means
// any status the frontend does not know about is treated as a settled charge —
// which is how `needs_new_card` would have rendered SuccessSlide and fired
// COMPLETED_CHECKOUT for a purchase that was never paid for.
//
// Inverting it to an allowlist makes the failure mode "we show an error for a
// charge that succeeded" instead of "we show success for a charge that did
// not". One classifier owns the rule so the four call sites cannot drift, the
// same way `src/utils/paymentErrors.ts` owns the already_subscribed rule.
// (Requested by Backend team, 2026-09-15 — second Stripe account rollout.)
//
// A narrow TypeScript union is deliberately NOT used at the wire boundary: the
// compiler cannot enforce exhaustiveness over a value the backend invents after
// this file was written, which is precisely the case being defended against.
//
// ─── An ABSENT status is settled; a PRESENT unrecognised one is not ──────────
// The one-time consultation charge returns a 200 carrying no `status` field at
// all, and it is the one path SuccessSlide never polls, so that response must
// still refresh metadata (requested by Pulkit, 2026-08-26). An absent status is
// therefore the documented success shape, not an unknown outcome.
//
// The fail-closed guard applies to a status that IS present and unrecognised —
// "pending", "needs_bank_redirect", anything added later. That is where the
// real risk lives, because every named non-settled status the backend has
// added so far (`needs_3ds`, `needs_new_card`) arrived that way.

export type CardPaymentOutcome = "settled" | "declined" | "needs_3ds" | "needs_new_card" | "unknown";

// The backend contract says "successful". "succeeded" is accepted alongside it
// because the codebase disagrees with itself about which spelling is real
// (chatSlice's own test uses "successful"; CheckoutSlide's test mock used
// "succeeded"), and the old `!== "failed"` check meant neither was ever
// exercised. The asymmetry decides it: wrongly rejecting the live spelling
// shows a payment error after money has already moved, while accepting both
// costs nothing — neither word can denote a non-settled outcome. Narrow this to
// one value only after confirming the live spelling with the backend team.
const SETTLED_STATUSES = new Set(["successful", "succeeded"]);

export const classifyCardPaymentStatus = (status: unknown): CardPaymentOutcome => {
  // Absent / null / empty: the statusless one-time-charge success shape.
  if (status === undefined || status === null) return "settled";
  if (typeof status !== "string") return "unknown";
  const normalized = status.trim().toLowerCase();
  if (normalized === "") return "settled";
  if (SETTLED_STATUSES.has(normalized)) return "settled";
  if (normalized === "failed") return "declined";
  if (normalized === "needs_3ds") return "needs_3ds";
  if (normalized === "needs_new_card") return "needs_new_card";
  return "unknown";
};

/** True only for a recognised settled charge (including the statusless shape). */
export const isSettledCardPayment = (responseData: unknown): boolean =>
  classifyCardPaymentStatus((responseData as { status?: unknown } | null | undefined)?.status) === "settled";
